import type { FocusEventHandler, KeyboardEventHandler, ReactNode, Ref } from "react";
import { Combobox } from "@base-ui/react/combobox";
import { SearchIcon } from "lucide-react";
import { inputVariants } from "@corpuscomplete/web-ui/components/input";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { cn } from "@corpuscomplete/web-ui/lib/utils";

type SearchComboboxItemValue = {
  value: string;
  label: string;
};

interface SearchComboboxBaseRootProps {
  children: ReactNode;
  value: string;
  onValueChange: (value: string) => void;
  onSelect?: (value: SearchComboboxItemValue) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  placeholder?: string;
  className?: string;
  portalContainer?: HTMLElement | null;
  open?: boolean;
  inputRef?: Ref<HTMLInputElement>;
}

function SearchComboboxBaseRoot({
  children,
  value,
  onValueChange,
  onSelect,
  onBlur,
  onKeyDown,
  placeholder = "Search...",
  className,
  portalContainer,
  open,
  inputRef,
}: SearchComboboxBaseRootProps) {
  return (
    <Combobox.Root<SearchComboboxItemValue>
      inputValue={value}
      modal={false}
      onInputValueChange={(inputValue, eventDetails) => {
        if (eventDetails.reason === "input-change" || eventDetails.reason === "input-clear") {
          onValueChange(inputValue);
        }
      }}
      onValueChange={(selectedValue) => {
        if (selectedValue) {
          onSelect?.(selectedValue);
        }
      }}
      open={open}
      openOnInputClick
    >
      <div className={cn("relative", className)}>
        <div className="relative">
          <Combobox.Input
            ref={inputRef}
            placeholder={placeholder}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            className={cn(inputVariants({ variant: "default" }), "pr-2 pl-8")}
          />
          <SearchIcon className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
        </div>
        <Combobox.Portal container={portalContainer ?? undefined}>
          <Combobox.Positioner sideOffset={4} className="isolate z-50 outline-none">
            <Combobox.Popup className="data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 ring-foreground/10 bg-popover text-popover-foreground z-50 mt-1 max-h-(--available-height) w-(--anchor-width) overflow-x-hidden overflow-y-auto rounded-md shadow-md ring-1 duration-100 outline-none">
              <Combobox.List>{children}</Combobox.List>
            </Combobox.Popup>
          </Combobox.Positioner>
        </Combobox.Portal>
      </div>
    </Combobox.Root>
  );
}

interface SearchComboboxBaseEmptyProps {
  children: ReactNode;
}

function SearchComboboxBaseEmpty({ children }: SearchComboboxBaseEmptyProps) {
  return (
    <div className="p-3 text-center">
      <Text variant="sm" className="text-muted-foreground">
        {children}
      </Text>
    </div>
  );
}

interface SearchComboboxBaseSectionProps {
  title: string;
  children: ReactNode;
  expandButton?: {
    label: string;
    onToggle: () => void;
  };
}

function SearchComboboxBaseSection({
  title,
  children,
  expandButton,
}: SearchComboboxBaseSectionProps) {
  return (
    <Combobox.Group>
      <Combobox.GroupLabel className="sticky top-0 flex items-center justify-between bg-muted/90 px-2 py-1">
        <Text variant="xs" className="font-semibold">
          {title}
        </Text>
        {expandButton ? (
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0"
            onMouseDown={(event) => {
              event.preventDefault();
              expandButton.onToggle();
            }}
          >
            <Text variant="xs">{expandButton.label}</Text>
          </Button>
        ) : null}
      </Combobox.GroupLabel>
      {children}
    </Combobox.Group>
  );
}

interface SearchComboboxBaseItemProps {
  value: SearchComboboxItemValue;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  rowKey?: string;
}

function SearchComboboxBaseItem({
  value,
  children,
  className,
  disabled = false,
  rowKey,
}: SearchComboboxBaseItemProps) {
  return (
    <Combobox.Item
      value={value}
      disabled={disabled}
      data-row-key={rowKey}
      className={cn(
        "cursor-pointer px-2 py-1.5 outline-none data-[highlighted]:bg-muted hover:bg-muted/50 data-[disabled]:pointer-events-none data-[disabled]:opacity-100",
        className,
      )}
    >
      {children}
    </Combobox.Item>
  );
}

export {
  SearchComboboxBaseRoot,
  SearchComboboxBaseEmpty,
  SearchComboboxBaseSection,
  SearchComboboxBaseItem,
  type SearchComboboxItemValue,
};
